import { useRef } from 'react';
import type { ChangeEventHandler } from 'react';
import styled from 'styled-components';

import { blue } from '@ant-design/colors';
import { PictureFilled } from '@ant-design/icons';

import { txt } from '../../locales/i18n';

export type ImageSelectorProps = {
	onSelect: (base64: string) => void | Promise<void>;
	onError: () => void | Promise<void>;
	onReset?: () => void | Promise<void>;
	imageSource: string | null;
	className?: string;
};

export const ImageSelector = (props: ImageSelectorProps) => {
	const selectorRef = useRef<HTMLInputElement>(null);

	const handleImageSelection: ChangeEventHandler<HTMLInputElement> = async (event) => {
		const [file = null] = Array.from(event.target.files ?? []);
		if (file === null) return;

		const reader = new FileReader();
		reader.readAsDataURL(file);

		reader.onerror = (error) => {
			console.error(error);
			props.onError();
		};

		reader.onload = () => {
			const imageSource = reader.result;
			if (imageSource === null) return props.onError();
			props.onSelect(String(imageSource));
		};
	};

	return (
		<ImageSelectorStyled
			onClick={() => selectorRef.current?.click()}
			className={props.className}
		>
			<input type="file" accept="image/*" ref={selectorRef} onChange={handleImageSelection} />
			{props.imageSource === null && (
				<span>
					{txt('global.select image')} <PictureFilled />
				</span>
			)}
			{props.imageSource !== null && <img alt="selector result" src={props.imageSource} />}
		</ImageSelectorStyled>
	);
};

const ImageSelectorStyled = styled.div`
	background-color: #eee;
	aspect-ratio: 5/3;
	width: 100%;
	border-radius: 12px;
	cursor: pointer;
	overflow: hidden;

	display: flex;
	align-items: center;
	justify-content: center;

	border: 1px solid transparent;
	transition: border 350ms;

	&:hover {
		border-color: ${blue.primary};
	}

	input {
		z-index: -1;
		visibility: hidden;
		position: absolute;
		top: 0;
	}

	span {
		font-size: 1.2rem;
		color: #999;
	}

	img {
		max-width: 100%;
		max-height: 100%;
		object-fit: contain;
	}
`;
